import React, { useState, useEffect, useRef } from 'react';
import GlobalFunction from './globalFunction';
import RmToolTip from './rmToolTip';
import './css/textControlModule.css';
import { Tile, Title, Body } from './style/tile.style';

export default function TextControlModule({ parameters, onSendMessage }) {
    const storageKey = parameters.config.module_name + '_' + parameters.config.event_screen_module_id;

    const [staticMessages, setStaticMessages] = useState(() => {
        let msgs = JSON.parse(localStorage.getItem(storageKey));
        if (msgs === null) {
            msgs = { staticMessage1: '', staticMessage2: '', staticMessage3: '', message4: '', flash: false };
            localStorage.setItem(storageKey, JSON.stringify(msgs));
        } else {
            msgs.message4 = '';
            if (!Object.prototype.hasOwnProperty.call(msgs, 'flash')) msgs.flash = false;
        }
        return msgs;
    });
    const [containerWidth, setContainerWidth] = useState(10);
    const [dataRow, setDataRow] = useState({ padding: 0, rowHeight: 34, fontSize: 14, clearButtonFontSize: 14 });

    const fontSizeCheckerRef = useRef(null);
    const fixedBlock1Ref = useRef(null);
    const fixedBlock2Ref = useRef(null);
    const containerRef = useRef(null);
    const containerWidthRef = useRef(10);

    function computeDataRow(width) {
        const row = {};
        row.padding = width < 200 ? '6px 5px' : '6px 12px';
        row.rowHeight = Math.min(34, Math.floor((parameters.containerHeight.container - 55) / 4));
        row.fontSize = Math.min(14, Math.floor(row.rowHeight * 0.6));
        const clearButtonWidth = width - 82;
        const message = parameters.config.description.length === 0 ? '' : parameters.config.description;
        row.clearButtonFontSize = GlobalFunction.getFontSize(message, clearButtonWidth, 30 - 6, fontSizeCheckerRef.current);
        if (row.clearButtonFontSize > 22) row.clearButtonFontSize = 22;
        return row;
    }

    useEffect(() => {
        const w = containerRef.current.clientWidth;
        setContainerWidth(w);
        containerWidthRef.current = w;
        setDataRow(computeDataRow(w));

        const handleResize = () => setDataRow(computeDataRow(containerWidthRef.current));
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        setDataRow(computeDataRow(containerWidthRef.current));
    }, [parameters.containerHeight.container]);

    function saveMessages(newMsgs) {
        setStaticMessages(newMsgs);
        localStorage.setItem(storageKey, JSON.stringify(newMsgs));
    }

    function handleClick(e) {
        const name = e.target.attributes.name.value;
        if (name !== 'clear' && staticMessages[name].length === 0) return;

        const messageObject = {
            sourceModuleName: parameters.config.module_name,
            event_grouping_seq: parameters.config.event_grouping_seq,
            customerID: parameters.userProfile.customer_id,
            siteID: parameters.userProfile.site_id,
            eventID: parameters.config.event_id,
        };
        if (name === 'clear') {
            messageObject.action = 'clear';
        } else {
            messageObject.message = staticMessages[name];
            messageObject.action = 'message';
            messageObject.flash = staticMessages.flash;
        }
        onSendMessage(messageObject);

        if (name === 'message4') {
            saveMessages({ ...staticMessages, message4: '' });
        }
    }

    function flashClicked() {
        saveMessages({ ...staticMessages, flash: !staticMessages.flash });
    }

    function clearTextMessages() {
        handleClick({ target: { attributes: { name: { value: 'clear' } } } });
    }

    function handleInputKeyDown(e) {
        if (e.key === 'Enter' && staticMessages[e.target.name].length > 0) {
            e.preventDefault();
            handleClick({ target: { attributes: { name: { value: e.target.name } } } });
        }
    }

    function handleChange(e) {
        if (e.target.name === 'toggle') {
            saveMessages({ ...staticMessages, flash: !staticMessages.flash });
        } else {
            if (e.target.value === ' ' || e.target.value === '\n') return;
            saveMessages({ ...staticMessages, [e.target.name]: e.target.value });
        }
    }

    const bodyHeight = parameters.containerHeight.container - 2 - 37 - 2;
    const topPanelStyle = {
        position: 'absolute', top: '1px', left: '1px', width: containerWidth - 2, textAlign: 'left', zIndex: 0,
        fontSize: dataRow.clearButtonFontSize + 'px', lineHeight: dataRow.rowHeight + 'px',
        cursor: 'pointer', backgroundColor: GlobalFunction.getPaletteColour(parameters.config.colour),
        paddingLeft: '37px', height: dataRow.rowHeight - 2 + 'px',
    };
    const flashStyle = { paddingLeft: '5px', paddingTop: '8px', paddingBottom: '0px', paddingRight: '2px', position: 'absolute', display: 'inline-block', fontSize: '21px', top: '0px', right: '2px', height: '30px', width: '35px' };
    const clearStyle = { paddingLeft: '5px', paddingTop: '8px', paddingBottom: '0px', paddingRight: '2px', position: 'absolute', display: 'inline-block', fontSize: '21px', top: '0px', right: '35px', height: '30px', width: '35px' };
    const topRowStyle = { height: '30px', width: containerWidth - 2, textAlign: 'left', borderBottom: '2px solid ' + parameters.colours.color + '66', fontSize: dataRow.clearButtonFontSize + 'px' };
    const variableHeight = bodyHeight - 5 - (dataRow.rowHeight * 3);
    const inputFieldStyle = {
        flexGrow: 1, marginLeft: '10px', height: dataRow.rowHeight + 'px', width: '20px',
        lineHeight: dataRow.rowHeight + 'px', paddingTop: '3%',
        backgroundColor: parameters.colours.backgroundColor,
        color: parameters.colours.color, borderStyle: 'none', borderRadius: 0,
        borderBottom: '1px solid ' + GlobalFunction.getPaletteColour(parameters.config.colour) + '66',
    };
    const textAreaStyle = {
        flexGrow: 1, marginLeft: '10px', height: (variableHeight - 10) + 'px', width: '20px',
        backgroundColor: parameters.colours.backgroundColor,
        borderRadius: 0, resize: 'none',
        color: parameters.colours.color, borderStyle: 'none',
        borderBottom: '1px solid ' + GlobalFunction.getPaletteColour(parameters.config.colour) + '66',
    };
    const sendButtonStyle = { flexGrow: 0, alignSelf: 'center', marginLeft: '6px', borderRadius: '50px', paddingLeft: '10px', paddingRight: '10px', paddingTop: '1px', paddingBottom: '1px', lineHeight: '1.2', fontSize: '11px', backgroundColor: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.25)', color: 'inherit', cursor: 'pointer' };
    const toolTip = staticMessages.flash ? <span>Screen<br />flash<br />on</span> : <span>Screen<br />flash<br />off</span>;

    return (
        <Tile className="outer-container" height={parameters.containerHeight.container - 4 + 'px'} ref={containerRef}>
            <Title ref={fixedBlock1Ref} fontSize={dataRow.clearButtonFontSize + 'px'}>
                <span style={{ paddingLeft: '5px' }}>{parameters.config.description}</span>
                <RmToolTip itemText={staticMessages.flash ? 'flash_on' : 'flash_off'}
                    itemType="icon" placement="bottom"
                    onClick={flashClicked}
                    style={flashStyle}
                    toolTipText={toolTip} />
                <RmToolTip itemText="highlight_off"
                    itemType="icon" placement="bottom"
                    onClick={clearTextMessages}
                    style={clearStyle}
                    toolTipText={<span>Clear all<br />messages</span>} />
            </Title>

            <Body fontSize={dataRow.fontSize + 'px'} height={bodyHeight + 'px'}>
                <div ref={fixedBlock2Ref}>
                    <div style={{ display: 'flex' }}>
                        <input type="text" name="staticMessage1" style={inputFieldStyle}
                            placeholder="Static message..." value={staticMessages.staticMessage1} onChange={handleChange} onKeyDown={handleInputKeyDown} />
                        <button name="staticMessage1" onClick={handleClick} disabled={staticMessages.staticMessage1.length === 0} style={sendButtonStyle}>Send</button>
                    </div>
                    <div style={{ display: 'flex' }}>
                        <input type="text" name="staticMessage2" style={inputFieldStyle}
                            placeholder="Static message..." value={staticMessages.staticMessage2} onChange={handleChange} onKeyDown={handleInputKeyDown} />
                        <button name="staticMessage2" onClick={handleClick} disabled={staticMessages.staticMessage2.length === 0} style={sendButtonStyle}>Send</button>
                    </div>
                    <div style={{ display: 'flex' }}>
                        <input type="text" name="staticMessage3" style={inputFieldStyle}
                            placeholder="Static message..." value={staticMessages.staticMessage3} onChange={handleChange} onKeyDown={handleInputKeyDown} />
                        <button name="staticMessage3" onClick={handleClick} disabled={staticMessages.staticMessage3.length === 0} style={sendButtonStyle}>Send</button>
                    </div>
                </div>
                <div style={{ display: 'flex' }}>
                    <textarea name="message4" style={textAreaStyle}
                        value={staticMessages.message4} onChange={handleChange} placeholder="Multiline message..." />
                    <button name="message4" onClick={handleClick} disabled={staticMessages.message4.length === 0} style={{ ...sendButtonStyle, alignSelf: 'flex-end', marginBottom: '7px' }}>Send</button>
                </div>
            </Body>

            <div style={{ position: 'fixed', top: '-2000px', left: '-2000px', backgroundColor: 'pink', whiteSpace: 'pre' }}
                ref={fontSizeCheckerRef} />
        </Tile>
    );
}
